import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const eur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const Stat = ({ label, value, warn, testId }) => (
  <div className="border border-slate-200 bg-white p-5" data-testid={testId}>
    <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{label}</div>
    <div className={`font-heading text-3xl font-light tracking-tighter mt-1 ${warn ? "text-red-600" : "text-slate-900"}`}>{value}</div>
  </div>
);

function PayDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ paid_on: "", amount: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) setF({ paid_on: new Date().toISOString().slice(0, 10), amount: String(row.open).replace(".", ",") }); }, [row]);
  const book = async () => {
    setBusy(true);
    try {
      await api.post(`/billing/avgs-payments/${row.invoice_id}`, { paid_on: f.paid_on, amount: Number(String(f.amount).replace(",", ".")) });
      toast.success("Zahlung gebucht");
      onDone();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="avgs-paid-dialog">
        <DialogHeader><DialogTitle>Rechnung als bezahlt buchen</DialogTitle></DialogHeader>
        {row && <p className="text-xs text-slate-500">{row.name} · Rechnung {row.invoice_number} · {row.kostentraeger || "—"} · offen {eur(row.open)}</p>}
        <label className="text-xs text-slate-600 flex flex-col">Zahlungseingang am<input type="date" value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} className="input-tech mt-1" data-testid="avgs-paid-date" /></label>
        <label className="text-xs text-slate-600 flex flex-col">Gezahlter Betrag (€)<input value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="input-tech mt-1" data-testid="avgs-paid-amount" /></label>
        <p className="text-[11px] text-slate-500">Weniger als der offene Betrag → „teilweise bezahlt“, der Rest bleibt offen. Es wird nichts versendet.</p>
        <button onClick={book} disabled={busy} className="w-full bg-slate-900 text-white py-3 text-sm font-medium hover:bg-black disabled:opacity-50" data-testid="avgs-paid-confirm">{busy ? "Bucht…" : "Zahlung buchen"}</button>
      </DialogContent>
    </Dialog>
  );
}

const STATUS = { open: "offen", partial: "teilweise bezahlt", paid: "bezahlt" };

export default function AvgsPayments() {
  const { user } = useAuth();
  const canUndo = user?.is_master_admin || user?.role === "admin" || (user?.all_roles || []).includes("admin") || (user?.billing_perms || []).includes("delete");
  const [d, setD] = useState(null);
  const [row, setRow] = useState(null);
  const [filter, setFilter] = useState("open");
  const load = () => api.get("/billing/avgs-payments").then((r) => setD(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-xs text-slate-400">Lädt…</div>;
  const today = new Date().toISOString().slice(0, 10);
  const rows = d.rows.filter((r) => filter === "all" || (filter === "open" ? r.status !== "paid" : r.status === "paid"));
  const undo = async (r) => {
    const last = r.payments[r.payments.length - 1];
    if (!window.confirm(`Zahlung vom ${formatDeShort(last.paid_on)} über ${eur(last.amount)} zurücknehmen?`)) return;
    try { await api.delete(`/billing/avgs-payments/${r.invoice_id}/last`); toast.success("Zahlung zurückgenommen"); load(); } catch (e) { err(e); }
  };
  return (
    <div data-testid="billing-avgs-payments">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <Stat label="Fällig & offen" value={eur(d.open_due)} testId="avgs-pay-open-due" warn={d.open_due > 0} />
        <Stat label="Bereits eingegangen" value={eur(d.paid)} testId="avgs-pay-paid" />
      </div>
      <div className="flex gap-1.5 mb-4" data-testid="avgs-pay-filter">
        {[["open", "Offen"], ["paid", "Bezahlt"], ["all", "Alle"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`avgs-pay-filter-${k}`} className={`px-3 py-1.5 text-xs border ${filter === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[1080px]">
          <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-overline text-slate-500 text-left whitespace-nowrap">
            <th className="px-4 py-3">Fällig am</th><th className="px-3">Teilnehmer</th><th className="px-3">Kundennr.</th><th className="px-3">Maßnahmenr.</th><th className="px-3">Zeitraum</th><th className="px-3">Leistungsträger</th><th className="px-3">Rechnung</th><th className="px-3 text-right">Betrag</th><th className="px-3">Status</th><th className="px-3"></th></tr></thead>
          <tbody>{rows.map((r) => {
            const overdue = r.status !== "paid" && r.date && r.date < today;
            return (
              <tr key={r.invoice_id} className={`border-t border-slate-100 ${overdue ? "bg-red-50 text-red-700" : ""}`} data-testid={`avgs-pay-row-${r.invoice_id}`}>
                <td className="px-4 py-2.5 font-mono text-xs">{formatDeShort(r.date)}</td>
                <td className="px-3"><Link to={`/avgs/${r.client_id}`} className="hover:underline">{r.name}</Link></td>
                <td className="px-3 text-xs font-mono">{r.customer_number || "—"}</td>
                <td className="px-3 text-xs font-mono">{r.measure_number || "—"}</td>
                <td className="px-3 text-xs font-mono whitespace-nowrap">{r.start ? `${formatDeShort(r.start)}–${formatDeShort(r.end)}` : "—"}</td>
                <td className="px-3 text-xs">{r.kostentraeger || "—"}</td>
                <td className="px-3 text-xs font-mono">{r.invoice_number}{r.is_correction ? " · Korrektur" : ""}{r.is_partial ? " · Rest" : ""}</td>
                <td className="px-3 text-right font-mono">{eur(r.amount)}{r.status === "partial" && <div className="text-[10px] text-amber-700">offen {eur(r.open)}</div>}</td>
                <td className="px-3 text-xs" data-testid={`avgs-pay-status-${r.invoice_id}`}>
                  {r.status === "paid" ? <span className="text-emerald-700">bezahlt {formatDeShort(r.payments[r.payments.length - 1].paid_on)}</span>
                    : r.status === "partial" ? <span className="text-amber-700">teilweise bezahlt ({eur(r.paid)})</span>
                    : overdue ? <span className="text-red-600">überfällig</span> : STATUS[r.status]}
                </td>
                <td className="px-3 text-right whitespace-nowrap">
                  {r.status !== "paid" && <button onClick={() => setRow(r)} className="px-3 py-1.5 text-xs bg-slate-900 text-white hover:bg-black" data-testid={`avgs-pay-btn-${r.invoice_id}`}>Bezahlt</button>}
                  {canUndo && r.payments.length > 0 && <button onClick={() => undo(r)} title="Zahlung zurücknehmen" className="ml-1 p-1.5 border border-slate-200 text-slate-500 hover:text-red-600" data-testid={`avgs-pay-undo-${r.invoice_id}`}><Undo2 size={12} /></button>}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
        {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="avgs-pay-empty">Keine AVGS-Rechnungen in dieser Ansicht.</div>}
      </div>
      <PayDialog row={row} onClose={() => setRow(null)} onDone={() => { setRow(null); load(); }} />
    </div>
  );
}
